import { FC, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import { loadKml } from './loadKml'

type KmlLayerProps = {
	options: {
		map?: google.maps.Map | null
		url?: string
		preserveViewport?: boolean
	}
}

const featureStyle = (feature: google.maps.Data.Feature): google.maps.Data.StyleOptions => {
	const style: google.maps.Data.StyleOptions = {}
	const stroke: unknown = feature.getProperty('stroke')
	const strokeOpacity: unknown = feature.getProperty('stroke-opacity')
	const strokeWidth: unknown = feature.getProperty('stroke-width')
	const fill: unknown = feature.getProperty('fill')
	const fillOpacity: unknown = feature.getProperty('fill-opacity')
	const icon: unknown = feature.getProperty('icon')
	const iconColor: unknown = feature.getProperty('icon-color')
	const visibility: unknown = feature.getProperty('visibility')

	if (typeof stroke === 'string') style.strokeColor = stroke
	if (typeof strokeOpacity === 'number') style.strokeOpacity = strokeOpacity
	if (typeof strokeWidth === 'number') style.strokeWeight = strokeWidth
	if (typeof fill === 'string') style.fillColor = fill
	if (typeof fillOpacity === 'number') style.fillOpacity = fillOpacity
	if (typeof icon === 'string') style.icon = icon
	else if (typeof iconColor === 'string') {
		style.icon = {
			path: google.maps.SymbolPath.CIRCLE,
			scale: 5,
			fillColor: iconColor,
			fillOpacity: 1,
			strokeWeight: 1,
		}
	}
	if (visibility === false || visibility === '0' || visibility === 0) style.visible = false
	return style
}

const KmlLayer: FC<KmlLayerProps> = ({ options: { map, url, preserveViewport = false } }) => {
	const intl = useIntl()
	const preserveViewportRef = useRef(preserveViewport)
	preserveViewportRef.current = preserveViewport

	useEffect(() => {
		if (!map || !url) return

		const controller = new AbortController()
		const layer = new google.maps.Data()
		const infoWindow = new google.maps.InfoWindow()
		let disposeResources: (() => void) | undefined
		layer.setStyle(featureStyle)
		const clickListener = layer.addListener('click', (event: google.maps.Data.MouseEvent) => {
			const content = document.createElement('div')
			const name: unknown = event.feature.getProperty('name')
			const description: unknown = event.feature.getProperty('description')
			if (typeof name === 'string') {
				const title = document.createElement('strong')
				title.textContent = name
				content.appendChild(title)
			}
			if (typeof description === 'string') {
				const details = document.createElement('div')
				// KML descriptions can contain untrusted HTML. Display text without executing it.
				details.textContent = new DOMParser().parseFromString(description, 'text/html').body.textContent
				content.appendChild(details)
			}
			infoWindow.setContent(content)
			infoWindow.setPosition(event.latLng)
			infoWindow.open({ map })
		})

		const load = async () => {
			try {
				const result = await loadKml(url, controller.signal)
				disposeResources = result.dispose
				if (controller.signal.aborted) {
					result.dispose()
					return
				}
				layer.addGeoJson(result.geoJson)
				layer.setMap(map)
				if (!preserveViewportRef.current) {
					const bounds = new google.maps.LatLngBounds()
					layer.forEach((feature) => feature.getGeometry()?.forEachLatLng((point) => bounds.extend(point)))
					if (!bounds.isEmpty()) map.fitBounds(bounds)
				}
			} catch (error) {
				if (controller.signal.aborted) return
				layer.setMap(null)
				disposeResources?.()
				disposeResources = undefined
				console.error('Error loading KML/KMZ layer:', error)
				toast.error(intl.formatMessage({ id: 'MAP.KML.LOAD.ERROR' }))
			}
		}
		load()

		return () => {
			controller.abort()
			clickListener.remove()
			infoWindow.close()
			layer.setMap(null)
			disposeResources?.()
		}
	}, [map, url, intl])

	return null
}

export { KmlLayer }
